import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { MonoLabel } from '../components/Editorial';
import SchoolPicker from '../components/SchoolPicker';
import SignupSchoolSection from '../components/SignupSchoolSection';
import { EMPTY_COLLEGE, collegeComplete, collegePayload } from '../utils/signupCollege';
import { useSchoolData } from '../hooks/useSchoolData';
import { registrationErrorMessage } from '../lib/apiError';
import { sanitizeNext } from '../lib/publicProfile';
import { canAccessAthleteDetail } from '../lib/athleteDetail';
import { getAttribution, clearAttribution } from '../lib/attribution';
import { HEARD_ABOUT_OPTIONS } from '../constants/heardAboutOptions';
import Button from '../components/Button';

// Step ids match iOS RegisterView.Step (Self) for parity.
const STEP_DOB = 'dob';
const STEP_CREDENTIALS = 'credentials';
const STEP_INFO = 'info';
const STEP_COPPA = 'coppa';
const STEP_VERIFY = 'verify';

const SPECIAL = /[!@#$%^&*(),.?":{}|<>]/;
const PW_RULES = [
  { key: 'length',    label: 'At least 8 characters',           test: (p) => p.length >= 8 },
  { key: 'uppercase', label: 'One uppercase letter',            test: (p) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'One lowercase letter',            test: (p) => /[a-z]/.test(p) },
  { key: 'special',   label: 'One special character (!@#$%^&*)', test: (p) => SPECIAL.test(p) },
];

function formatPhone(raw) {
  const d = (raw || '').replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function isValidPhone(formatted) {
  const d = (formatted || '').replace(/\D/g, '');
  return d.length === 10;
}

function computeAge(dobIso) {
  if (!dobIso) return null;
  const dob = new Date(dobIso);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age -= 1;
  return age;
}

/**
 * Multi-step signup form matching iOS RegisterView. The role prop chooses
 * between athlete ("player") and coach copy/behavior; the parent-creates-
 * athlete flow lives in ParentRegister.jsx because it collects a fully
 * different set of fields.
 */
export default function Register({ role = 'player' }) {
  const { registerFull } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { exactMatch } = useSchoolData(role);

  // A coach who chose "Sign up" instead of "Log in" from a public athlete
  // profile has been promised, on the value card itself, that we "redirect back
  // here". LoginPage honours that; without this the signup arm of the same
  // funnel dropped it and landed on /dashboard.
  //
  // Re-validated here, not trusted: `next` has crossed two page boundaries
  // (/login -> /register -> /register/coach) and is re-read from this page's
  // own URL, which anybody can compose. Every hop sanitizes for itself.
  const safeNext = sanitizeNext(searchParams.get('next'));

  const [step, setStep] = useState(STEP_DOB);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Field state — names mirror RegisterView's @State vars for cross-platform grepability.
  const [dob, setDob] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [school, setSchool] = useState('');
  const [schoolCity, setSchoolCity] = useState('');
  const [schoolState, setSchoolState] = useState('');
  const [schoolZip, setSchoolZip] = useState('');
  const [college, setCollege] = useState(EMPTY_COLLEGE);
  const [guardianName, setGuardianName] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  // SMS consent is deliberately SEPARATE from termsAccepted and starts
  // unchecked. Toll-free carrier vetting (and TCPA express written consent)
  // both require the SMS opt-in to be unbundled from any other agreement and
  // never pre-ticked, so this cannot be folded back into the terms checkbox.
  const [smsConsent, setSmsConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [heardAbout, setHeardAbout] = useState('');

  // If the user arrives at /register/athlete or /register/coach without
  // going through the role picker, surface a back link to it.
  const fromRolePicker = location.state?.fromRolePicker === true;

  const age = useMemo(() => computeAge(dob), [dob]);
  const isPlayer = role === 'player';
  const isCoach = role === 'coach';
  const heading = isCoach ? 'Coach sign up' : 'Athlete sign up';

  const advanceFromDob = () => {
    setError('');
    if (!dob) {
      setError('Date of birth is required');
      return;
    }
    if (age !== null && age < 13) {
      setStep(STEP_COPPA);
      return;
    }
    setStep(STEP_CREDENTIALS);
  };

  const advanceFromCredentials = () => {
    setError('');
    const e = email.trim().toLowerCase();
    if (!e) { setError('Email is required'); return; }
    if (!/^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(e)) {
      setError('Enter a valid email address');
      return;
    }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    const failed = PW_RULES.find((r) => !r.test(password));
    if (failed) { setError(failed.label + ' is required'); return; }
    setStep(STEP_INFO);
  };

  const submitInfo = async () => {
    setError('');
    if (!firstName.trim()) { setError('First name is required'); return; }
    if (!lastName.trim()) { setError('Last name is required'); return; }
    if (!phone.trim()) { setError('Phone number is required'); return; }
    if (!isValidPhone(phone)) { setError('Enter a valid 10-digit US phone number'); return; }
    if (isPlayer && college.college) {
      if (!collegeComplete(college)) { setError('Choose your college and years of eligibility'); return; }
    } else if (!school.trim()) { setError('School is required'); return; }
    if (!termsAccepted) {
      setError('Please accept the Privacy Policy and Terms of Use to continue.');
      return;
    }

    // Match iOS RegisterView.submitRegistration: enrich empty city/state/zip
    // from the bundled school data when the typed name uniquely resolves.
    let resolvedCity = schoolCity;
    let resolvedState = schoolState;
    let resolvedZip = schoolZip;
    if ((!resolvedCity || !resolvedState) && school.trim()) {
      const match = exactMatch(school.trim());
      if (match) {
        if (!resolvedCity) resolvedCity = match.city || '';
        if (!resolvedState) resolvedState = match.state || '';
        if (!resolvedZip) resolvedZip = match.zip || '';
      }
    }

    setLoading(true);
    try {
      const result = await registerFull({
        email: email.trim().toLowerCase(),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim(),
        date_of_birth: dob,
        school: school.trim(),
        school_city: resolvedCity || null,
        school_state: resolvedState || null,
        school_zip: resolvedZip || null,
        ...(isPlayer ? collegePayload(college) || {} : {}),
        requested_role: role,
        guardian1_name: isPlayer && age !== null && age < 18 ? (guardianName.trim() || null) : null,
        guardian1_phone: isPlayer && age !== null && age < 18 ? (guardianPhone.trim() || null) : null,
        guardian1_email: isPlayer && age !== null && age < 18 ? (guardianEmail.trim() || null) : null,
        terms_accepted: true,
        // Sent as the real checkbox value, never hardcoded true — this is the
        // audit trail a carrier traceback asks for.
        sms_consent: smsConsent,
        acquisition: getAttribution() || undefined,
        heard_about: heardAbout || undefined,
      });
      // The record has done its job — it is now on the user row server-side.
      // Clearing it stops a shared browser (camp walk-in kiosk, family iPad)
      // from attributing every later signup to the first visitor's ad click
      // for the next 90 days. Only on success: a failed submit must leave the
      // record intact so the retry still carries it.
      clearAttribution();
      // Auto-login on success — registerFull returns the logged-in user.
      // If email is unverified, show the verify-pending screen; otherwise
      // hand off to the role-aware landing page. Athletes go to /upgrade
      // (paywall) since coaches don't see them until they subscribe.
      if (result?.unverified) {
        // No session yet, so there is nowhere to redirect TO — the user has to
        // click the emailed link first. `next` is dropped rather than stashed;
        // see the note where safeNext is read.
        setStep(STEP_VERIFY);
      } else {
        // Same allow-list the login gate uses: `next` only ever points at a
        // coach-gated route, so handing it to an account that cannot open it
        // would bounce off RoleProtectedRoute instead of landing anywhere.
        const gated = safeNext && canAccessAthleteDetail(result?.user?.role);
        navigate(gated ? safeNext : (isCoach ? '/dashboard' : '/upgrade'),
                 { state: { from: 'register' } });
      }
    } catch (err) {
      setError(registrationErrorMessage(err));
    }
    setLoading(false);
  };

  return (
    <div className="ed-auth">
      <div className="ed-auth__hero">
        <div>
          <MonoLabel onDark>JOIN PROZPECTS</MonoLabel>
          <h1 className="ed-auth__hero-headline" style={{ marginTop: 16 }}>
            {isCoach ? <>Find your<br /><em>next class.</em></> : <>Get on the<br /><em>recruiting board.</em></>}
          </h1>
        </div>
        <p className="ed-auth__hero-quote">
          {isCoach
            ? '"The fastest way to evaluate verified athletes you wouldn\'t otherwise see."'
            : '"Coaches search verified athletes first. Build a profile, get verified at a camp, get found."'}
        </p>
      </div>

      <div className="ed-auth__form-wrap">
        <div className="ed-auth__form">
          <div className="auth-logo" style={{ marginBottom: 24 }}>
            <img src="/logo-wordmark.png" alt="ProZpects" style={{ maxHeight: 36 }} />
          </div>
          <MonoLabel>{heading.toUpperCase()}</MonoLabel>
          <h2 style={{
            fontFamily: 'var(--ed-font-serif)',
            fontSize: 36, fontWeight: 400, letterSpacing: '-0.015em',
            margin: '8px 0 12px',
          }}>
            {step === STEP_VERIFY ? 'Check your email.' : 'Sign up.'}
          </h2>

          {step !== STEP_VERIFY && step !== STEP_COPPA && (
            <StepDots
              current={step === STEP_DOB ? 1 : step === STEP_CREDENTIALS ? 2 : 3}
              total={3}
            />
          )}

          {step === STEP_DOB && (
            <DobStep
              dob={dob}
              setDob={setDob}
              error={error}
              loading={loading}
              onContinue={advanceFromDob}
              fromRolePicker={fromRolePicker}
            />
          )}

          {step === STEP_COPPA && <CoppaBlock />}

          {step === STEP_CREDENTIALS && (
            <CredentialsStep
              email={email} setEmail={setEmail}
              password={password} setPassword={setPassword}
              confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
              showPassword={showPassword} setShowPassword={setShowPassword}
              showConfirmPassword={showConfirmPassword} setShowConfirmPassword={setShowConfirmPassword}
              error={error}
              loading={loading}
              onBack={() => { setError(''); setStep(STEP_DOB); }}
              onContinue={advanceFromCredentials}
            />
          )}

          {step === STEP_INFO && (
            <InfoStep
              role={role}
              age={age}
              firstName={firstName} setFirstName={setFirstName}
              lastName={lastName} setLastName={setLastName}
              phone={phone} setPhone={setPhone}
              school={school}
              schoolCity={schoolCity}
              schoolState={schoolState}
              schoolZip={schoolZip}
              college={college} setCollege={setCollege}
              clearHighSchool={() => { setSchool(''); setSchoolCity(''); setSchoolState(''); setSchoolZip(''); }}
              setSchoolFields={({ name, city, state, zip }) => {
                setSchool(name);
                setSchoolCity(city);
                setSchoolState(state);
                setSchoolZip(zip);
              }}
              guardianName={guardianName} setGuardianName={setGuardianName}
              guardianPhone={guardianPhone} setGuardianPhone={setGuardianPhone}
              guardianEmail={guardianEmail} setGuardianEmail={setGuardianEmail}
              heardAbout={heardAbout} setHeardAbout={setHeardAbout}
              termsAccepted={termsAccepted} setTermsAccepted={setTermsAccepted}
              smsConsent={smsConsent} setSmsConsent={setSmsConsent}
              error={error}
              loading={loading}
              onBack={() => { setError(''); setStep(STEP_CREDENTIALS); }}
              onSubmit={submitInfo}
            />
          )}

          {step === STEP_VERIFY && <VerifyPendingInline email={email} />}

          <div className="auth-footer" style={{ marginTop: 32 }}>
            Already have an account? <Link to="/login">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Step views ----------

function StepDots({ current, total }) {
  return (
    <div className="register-step-dots" aria-hidden="true" style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 8, height: 8, borderRadius: 4,
            background: i + 1 <= current ? 'var(--ed-color-primary, #0066cc)' : 'var(--ed-color-border, #ddd)',
          }}
        />
      ))}
    </div>
  );
}

function DobStep({ dob, setDob, error, loading, onContinue, fromRolePicker }) {
  return (
    <div className="auth-form">
      {error && <div className="auth-error" role="alert">{error}</div>}

      <div className="auth-field">
        <label htmlFor="register-dob">Date of birth</label>
        <input
          id="register-dob"
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          disabled={loading}
          max={new Date().toISOString().slice(0, 10)}
          required
        />
        <small style={{ color: 'var(--ed-color-text-secondary, #555)' }}>
          We use your DOB to set up the right account and to keep things legal — never shown publicly.
        </small>
      </div>

      <Button type="button" variant="primary" className="auth-btn" disabled={loading || !dob} onClick={onContinue}>
        Continue
      </Button>

      {!fromRolePicker && (
        <div style={{ marginTop: 12, fontSize: 13, color: 'var(--ed-color-text-secondary, #555)' }}>
          <Link to="/register">← Pick a different account type</Link>
        </div>
      )}
    </div>
  );
}

function CoppaBlock() {
  return (
    <div style={{ padding: '24px 0' }}>
      <h3 style={{ margin: '0 0 12px' }}>Almost there.</h3>
      <p style={{ marginBottom: 16, lineHeight: 1.5 }}>
        Athletes under 13 need a parent or guardian to sign up on their behalf — it's a federal rule (COPPA),
        not a ProZpects rule.
      </p>
      <Button to="/register/parent" variant="primary" className="auth-btn">
        Sign up as a parent →
      </Button>
    </div>
  );
}

function CredentialsStep({
  email, setEmail,
  password, setPassword,
  confirmPassword, setConfirmPassword,
  showPassword, setShowPassword,
  showConfirmPassword, setShowConfirmPassword,
  error, loading, onBack, onContinue,
}) {
  const pwStarted = password.length > 0;
  return (
    <form className="auth-form" onSubmit={(e) => { e.preventDefault(); onContinue(); }}>
      {error && <div className="auth-error" role="alert">{error}</div>}

      <div className="auth-field">
        <label htmlFor="register-email">Email</label>
        <input
          id="register-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
          autoComplete="email"
        />
      </div>

      <div className="auth-field">
        <label htmlFor="register-password">Password</label>
        <div className="auth-password-wrapper">
          <input
            id="register-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            autoComplete="new-password"
            aria-describedby="register-password-reqs"
          />
          <Button type="button" variant="ghost" size="sm" className="auth-password-toggle" onClick={() => setShowPassword(!showPassword)}>
            {showPassword ? 'Hide' : 'Show'}
          </Button>
        </div>
        <div className="pw-requirements" id="register-password-reqs" aria-live="polite">
          {PW_RULES.map(({ key, label, test }) => {
            const met = test(password);
            return (
              <div key={key} className={`pw-req${pwStarted ? (met ? ' pw-req--met' : ' pw-req--unmet') : ''}`}>
                <span className="pw-req__icon">{met ? '✓' : '○'}</span>{label}
              </div>
            );
          })}
        </div>
      </div>

      <div className="auth-field">
        <label htmlFor="register-confirm-password">Confirm password</label>
        <div className="auth-password-wrapper">
          <input
            id="register-confirm-password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            autoComplete="new-password"
          />
          <Button type="button" variant="ghost" size="sm" className="auth-password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
            {showConfirmPassword ? 'Hide' : 'Show'}
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="button" variant="secondary" className="auth-btn auth-btn--secondary" onClick={onBack} disabled={loading}>
          Back
        </Button>
        <Button type="submit" variant="primary" className="auth-btn" disabled={loading} style={{ flex: 1 }}>
          Continue
        </Button>
      </div>
    </form>
  );
}

function InfoStep({
  role, age,
  firstName, setFirstName,
  lastName, setLastName,
  phone, setPhone,
  school, schoolCity, schoolState, schoolZip, setSchoolFields,
  college, setCollege, clearHighSchool,
  guardianName, setGuardianName,
  guardianPhone, setGuardianPhone,
  guardianEmail, setGuardianEmail,
  heardAbout, setHeardAbout,
  termsAccepted, setTermsAccepted,
  smsConsent, setSmsConsent,
  error, loading, onBack, onSubmit,
}) {
  const isPlayer = role === 'player';
  const isCoach = role === 'coach';
  const needsGuardian = isPlayer && age !== null && age < 18;

  return (
    <form className="auth-form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
      {error && <div className="auth-error" role="alert">{error}</div>}

      <div className="auth-name-row">
        <div className="auth-field">
          <label htmlFor="register-first-name">First name</label>
          <input
            id="register-first-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            disabled={loading}
            autoComplete="given-name"
          />
        </div>
        <div className="auth-field">
          <label htmlFor="register-last-name">Last name</label>
          <input
            id="register-last-name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            disabled={loading}
            autoComplete="family-name"
          />
        </div>
      </div>

      <div className="auth-field">
        <label htmlFor="register-phone">Phone number</label>
        <input
          id="register-phone"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(formatPhone(e.target.value))}
          required
          disabled={loading}
          autoComplete="tel-national"
          inputMode="tel"
          placeholder="(555) 555-5555"
        />
      </div>

      {isCoach ? (
        <div className="auth-field">
          <label>School</label>
          <SchoolPicker
            value={school}
            city={schoolCity}
            state={schoolState}
            zip={schoolZip}
            onChange={setSchoolFields}
            role={role}
            disabled={loading}
          />
        </div>
      ) : (
        <SignupSchoolSection value={college} onChange={setCollege} onSwitchToCollege={clearHighSchool} disabled={loading}>
          <label>High school</label>
          <SchoolPicker
            value={school}
            city={schoolCity}
            state={schoolState}
            zip={schoolZip}
            onChange={setSchoolFields}
            role={role}
            disabled={loading}
          />
        </SignupSchoolSection>
      )}

      {isCoach && (
        <div className="register-coach-notice" style={{
          padding: '8px 12px', background: 'rgba(0,102,204,0.06)',
          borderRadius: 6, fontSize: 13, color: 'var(--ed-color-text-secondary, #555)',
          marginTop: -4,
        }}>
          Coach accounts are reviewed before full access is granted.
        </div>
      )}

      {needsGuardian && (
        <fieldset className="register-guardian">
          <legend style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Parent or guardian</legend>
          <p style={{ fontSize: 13, color: 'var(--ed-color-text-secondary, #555)', marginTop: 0 }}>
            We email a one-tap approval so a guardian can manage your profile alongside you.
          </p>
          <div className="auth-field">
            <label htmlFor="register-guardian-name">Guardian name</label>
            <input
              id="register-guardian-name"
              type="text"
              value={guardianName}
              onChange={(e) => setGuardianName(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="auth-field">
            <label htmlFor="register-guardian-phone">Guardian phone</label>
            <input
              id="register-guardian-phone"
              type="tel"
              value={guardianPhone}
              onChange={(e) => setGuardianPhone(formatPhone(e.target.value))}
              disabled={loading}
              inputMode="tel"
              placeholder="(555) 555-5555"
            />
          </div>
          <div className="auth-field">
            <label htmlFor="register-guardian-email">Guardian email</label>
            <input
              id="register-guardian-email"
              type="email"
              value={guardianEmail}
              onChange={(e) => setGuardianEmail(e.target.value)}
              disabled={loading}
            />
          </div>
        </fieldset>
      )}

      <div className="auth-field">
        <label htmlFor="register-heard-about">
          How did you hear about us? <span style={{ color: '#888', fontWeight: 'normal' }}>(optional)</span>
        </label>
        <select
          id="register-heard-about"
          value={heardAbout}
          onChange={(e) => setHeardAbout(e.target.value)}
          disabled={loading}
        >
          <option value="">Prefer not to say</option>
          {HEARD_ABOUT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <label className="auth-terms" style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        fontSize: 14, lineHeight: 1.4, marginTop: 4,
        color: 'var(--ed-color-text-secondary, #555)',
      }}>
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          disabled={loading}
          style={{ marginTop: 3 }}
        />
        <span>
          I agree to the{' '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
          {' '}and{' '}
          <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</Link>.
        </span>
      </label>

      {/* Standalone SMS opt-in. Optional and unchecked by default — the account
          itself does not depend on it, and it is intentionally NOT wired to the
          submit button's disabled state (unlike termsAccepted). The disclosure
          wording below is the sentence toll-free carrier vetting requires to be
          visible in the opt-in workflow; do not trim it. */}
      <label className="auth-sms-consent" htmlFor="register-sms-consent" style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        fontSize: 12.5, lineHeight: 1.45, marginTop: 8,
        padding: '10px 12px', borderRadius: 8,
        border: '1px solid var(--ed-color-border, #d7dde5)',
        color: 'var(--ed-color-text-secondary, #555)',
      }}>
        <input
          id="register-sms-consent"
          type="checkbox"
          checked={smsConsent}
          onChange={(e) => setSmsConsent(e.target.checked)}
          disabled={loading}
          style={{ marginTop: 3 }}
        />
        <span>
          <strong style={{ display: 'block', marginBottom: 2, fontSize: 13 }}>
            Text me updates about my ProZpects account (optional)
          </strong>
          I agree to receive SMS text messages from ProZpects about my account and my
          recruiting activity at the mobile number I entered above. Consent is not a
          condition of creating an account or of any purchase. Message frequency may vary.
          Standard message and data rates may apply. Reply STOP to opt out. Reply HELP for
          assistance. Your mobile information will not be sold or shared with third parties
          for promotional or marketing purposes. See our{' '}
          <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
          {' '}and{' '}
          <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</Link>.
        </span>
      </label>

      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="button" variant="secondary" className="auth-btn auth-btn--secondary" onClick={onBack} disabled={loading}>
          Back
        </Button>
        <Button type="submit" variant="primary" className="auth-btn" disabled={loading || !termsAccepted} style={{ flex: 1 }}>
          {loading ? 'Creating account…' : 'Create account'}
        </Button>
      </div>
    </form>
  );
}

function VerifyPendingInline({ email }) {
  const { resendVerificationEmail } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  return (
    <div className="register-verify-pending">
      <p style={{ lineHeight: 1.5 }}>
        We sent a verification link to <strong>{email}</strong>. Tap it to finish setting up your account.
      </p>
      <Button
        type="button"
        variant="secondary"
        className="auth-btn auth-btn--secondary"
        disabled={sending || sent}
        onClick={async () => {
          setSending(true);
          try {
            await resendVerificationEmail?.(email);
            setSent(true);
          } catch {
            // soft-fail; user can try again
          }
          setSending(false);
        }}
      >
        {sent ? 'Email sent — check your inbox' : sending ? 'Sending…' : 'Resend email'}
      </Button>
    </div>
  );
}
